<template>
  <v-container fluid class="max-width-container pa-0 py-6">
    <v-row justify="center" class="gap-y-6">

      <!-- LEFT COLUMN: Equipment summary -->
      <v-col cols="12" md="5">
        <CheckoutSummary :cart-total-monthly-price="cartTotalMonthlyPrice" @remove-item="$emit('remove-item', $event)" />
      </v-col>

      <!-- RIGHT COLUMN: Contact Form -->
      <v-col cols="12" md="7">
        <v-card class="pa-8 border-card shadow-form h-100" rounded="xl" style="background: white;">
          <!-- Header and Title (Hidden during loading) -->
          <template v-if="!preapprovalLoading">
            <div class="text-left mb-6">
              <h2 class="text-h5 font-weight-black text-blue-grey-darken-4 mb-2">Where should we send your quote?</h2>
              <p class="text-body-2 text-grey-darken-1 mb-0">
                We will email and text your detailed equipment quote. No spam.
              </p>
            </div>
          </template>

          <!-- Submission Loading State -->
          <div v-if="preapprovalLoading" class="text-center py-12">
            <v-progress-circular indeterminate color="primary" size="64" width="6" class="mb-6" />
            <h3 class="text-h5 font-weight-black text-blue-grey-darken-4 mb-2">
              Submitting quote request
            </h3>
            <p class="text-body-2 text-grey-darken-1">
              Finalizing your configuration details...
            </p>
          </div>

          <v-form v-else ref="contact_form" v-model="contactFormValid" @submit.prevent="$emit('submit')">
            <!-- Error Alert -->
            <v-alert v-if="preapprovalError" type="error" variant="tonal" class="mb-6 text-left" rounded="lg">
              {{ preapprovalError }}
            </v-alert>

            <!-- Contract Status Question Toggle -->
            <div class="mb-6">
              <span class="input-label font-weight-black text-grey-darken-2 d-block mb-2">
                DO YOU CURRENTLY HAVE AN ACTIVE CONTRACT ON YOUR COPIER?
              </span>
              <v-row dense>
                <v-col cols="12" sm="6">
                  <div
                    class="contract-status-card pa-4 rounded-lg cursor-pointer d-flex align-center h-100 transition-all"
                    :class="{ 'card-active': form.contract_status === 'out_of_contract' }"
                    @click="form.contract_status = 'out_of_contract'"
                  >
                    <v-icon
                      :icon="form.contract_status === 'out_of_contract' ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'"
                      :color="form.contract_status === 'out_of_contract' ? 'primary' : 'grey-darken-1'"
                      size="22"
                      class="me-3 flex-shrink-0"
                    />
                    <div class="flex-grow-1">
                      <div class="font-weight-black text-slate-800 text-body-2 mb-1">No Active Contract</div>
                      <div class="text-caption text-slate-500 font-weight-medium line-height-tight">New equipment / Out of contract</div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <div
                    class="contract-status-card pa-4 rounded-lg cursor-pointer d-flex align-center h-100 transition-all"
                    :class="{ 'card-active': form.contract_status === 'active' }"
                    @click="form.contract_status = 'active'"
                  >
                    <v-icon
                      :icon="form.contract_status === 'active' ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank'"
                      :color="form.contract_status === 'active' ? 'primary' : 'grey-darken-1'"
                      size="22"
                      class="me-3 flex-shrink-0"
                    />
                    <div class="flex-grow-1">
                      <div class="font-weight-black text-slate-800 text-body-2 mb-1">Yes, Active Contract</div>
                      <div class="text-caption text-slate-500 font-weight-medium line-height-tight">Looking to upgrade or service</div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>

            <v-row class="gap-y-4">
              <v-col cols="12">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">LEGAL BUSINESS NAME</span>
                <v-text-field
                  v-model="form.contact.business_name"
                  placeholder="e.g. Northgate Dental Group LLC"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">FIRST NAME</span>
                <v-text-field
                  v-model="form.contact.first_name"
                  placeholder="e.g. Maria"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">LAST NAME</span>
                <v-text-field
                  v-model="form.contact.last_name"
                  placeholder="e.g. Reyes"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">WORK EMAIL</span>
                <v-text-field
                  v-model="form.contact.email"
                  placeholder="e.g. maria@northgate.com"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  :rules="[rules.required, rules.email]"
                />
              </v-col>

              <v-col cols="12" sm="6">
                <span class="input-label font-weight-black text-grey-darken-2 d-block mb-1">MOBILE NUMBER</span>
                <v-text-field
                  v-model="form.contact.phone"
                  placeholder="e.g. (813) 555-0140"
                  variant="outlined"
                  density="comfortable"
                  rounded="lg"
                  color="primary"
                  hide-details
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>

            <v-btn
              type="submit"
              :color="ConfigElementsColor"
              block
              rounded="lg"
              size="large"
              elevation="0"
              class="text-none font-weight-bold cta-btn mt-6 mb-3"
            >
              Request pricing quote
            </v-btn>

            <v-btn
              variant="outlined"
              color="primary"
              block
              rounded="lg"
              size="large"
              class="text-none font-weight-bold"
              @click="configurator_step = 'summary'"
            >
              Back
            </v-btn>
          </v-form>
        </v-card>
      </v-col>

    </v-row>
  </v-container>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import CheckoutSummary from '@/components/configurator/CheckoutSummary.vue'

defineProps<{
  cartTotalMonthlyPrice: number
  preapprovalLoading: boolean
  preapprovalError: string
}>()

defineEmits<{
  (e: 'remove-item', idx: number): void
  (e: 'submit'): void
}>()

const store = useGlobalsStore()
const { form, configurator_step, ConfigElementsColor } = storeToRefs(store)

const contactFormValid = ref(false)

const rules = {
  required: (v: any) => !!v || 'Field is required',
  email: (v: string) => /.+@.+\..+/.test(v) || 'E-mail must be valid'
}
</script>

<style scoped lang="scss">
.max-width-container {
  max-width: 1200px;
}
.border-card {
  border: 1px solid rgba(148, 163, 184, 0.2) !important;
}
.shadow-form {
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05);
}
.contract-status-card {
  border: 1px solid rgba(148, 163, 184, 0.25);
  background-color: #f8fafc;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #3b82f6;
    background-color: #ffffff;
  }

  &.card-active {
    border-color: #2563eb !important;
    background-color: #eff6ff !important;
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.08);
  }
}
.input-label {
  font-size: 0.72rem;
  letter-spacing: 0.5px;
}
.text-slate-800 {
  color: #1e293b;
}
.text-slate-500 {
  color: #64748b;
}
.line-height-tight {
  line-height: 1.25;
}
</style>
